import { ChartTooltip, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltip';
import { headerFormatter } from '@Components/Charts/ChartUtils';
import { useCompanyInfo } from '../../../../utils/hooks/useCompanyInfo';

export const TooltipShape = {
  CIRCLE: 'circle',
  SQUARE: 'square',
  LINE: 'line'
} as const;
export type TooltipShapeType = (typeof TooltipShape)[keyof typeof TooltipShape];

/** A single entry in a Recharts tooltip payload, narrowed to the fields these charts read. */
export interface TooltipPayloadItem {
  name?: string;
  value: number;
  stroke?: string;
  /** The original data row Recharts attaches to each payload entry. */
  payload: Record<string, number>;
}

/**
 * Maps a Recharts tooltip payload to ChartTooltip items, sorted by descending absolute
 * value. Sorts on the raw numeric value (before formatting) so ordering is stable
 * regardless of whether a formatter is supplied.
 */
export const buildSortedTooltipItems = (
  payload: TooltipPayloadItem[],
  getLabel: (item: TooltipPayloadItem) => string,
  formatValue: (value: number) => string | number
): PayloadItem[] =>
  [...payload]
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .map(item => ({
      label: getLabel(item),
      value: formatValue(item.value),
      color: item.stroke
    }));

interface DateGroupedTooltipContentProps {
  label?: string | number;
  payload?: TooltipPayloadItem[];
  dateGrouping?: string;
  getLabel: (item: TooltipPayloadItem) => string;
  formatValue: (value: number) => string | number;
  shape?: TooltipShapeType;
  reverseItems?: boolean;
}

/**
 * Shared tooltip body for the date-grouped GeoMMM Results charts (Actual vs Predicted,
 * Spend, and Outcome & Response's stacked-area view). Recharts injects `label`/`payload`;
 * each caller supplies its own per-series label + value mapping. Extracted from three
 * byte-identical inline copies. `dateGrouping` is always truthy at the render sites, so the
 * guard matches all three originals.
 */
export const DateGroupedTooltipContent = ({
  label,
  payload,
  dateGrouping,
  getLabel,
  formatValue,
  shape,
  reverseItems
}: DateGroupedTooltipContentProps) => {
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  if (!payload || !label || !dateGrouping) {
    return null;
  }
  const headerLabel = headerFormatter(dateGrouping, String(label), fiscalCal);
  const items = buildSortedTooltipItems(payload, getLabel, formatValue);
  return (
    <ChartTooltip
      headerLabel={headerLabel}
      items={items}
      shape={shape}
      reverseItems={reverseItems}
    />
  );
};
